import React from 'react';
import { ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { useState } from 'react';
import { cameraService } from '../../../services/central-api/cameras';
import MyDataTable from '../../../components/MyDataTable';
import moment from 'moment';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { useQuery } from '@tanstack/react-query';
import DropdownButton from '../../../components/DropdownButton';
import { RootNavigation } from '../../../Routes';

interface Props {
    navigation: RootNavigation;
}

function VideoAnalysis({ navigation }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CamerasParamList, 'VideoAnalysis'>>();

    const [filters, setFilters] = useFilters<CamerasParamList['VideoAnalysis']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['videoAnalysis', filters],
        queryFn: () => cameraService.getPaginatedVideoAnalysis(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const { data } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Container>
            <Header title={translate('zeusAI')} />
            <Card>
                <DataTableHeader
                    title={translate('registeredVideoAnalysis')}
                    zIndex={2}
                    filter={<Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('status'),
                                cell: row =>
                                    <View style={{ flexDirection: 'row', columnGap: 5, alignItems: 'center' }}>
                                        {row.status == 'pending' ?
                                            <ActivityIndicator size={16} color={theme.colors.loadingColor} /> :
                                            <FontAwesomeIcon style={styles.statusStyle(row.status)} fontSize={16} icon={row.status == 'success' ? faCheckCircle : faTimesCircle} />}
                                    </View>,
                                wrap: true,
                                grow: 0
                            },
                            {
                                name: translate('analyzedCamera'),
                                selector: row => row.camera.title,
                                wrap: true,
                            },
                            {
                                name: translate('requester'),
                                selector: row => row.user.name,
                                wrap: true,
                            },
                            {
                                name: translate('request'),
                                selector: row => row.prompt,
                                wrap: true
                            },
                            {
                                name: translate('analysisPeriod'),
                                selector: row => `${moment(+row.msStart).format('DD/MM/YYYY')} - ${moment(+row.msStart).format('HH:mm')} ${translate('to')} ${moment(+row.msFinish).format('HH:mm')}`,
                                wrap: true
                            },
                            {
                                name: translate('requestAnalysisDate'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                                wrap: true
                            },
                            {
                                button: true,
                                wrap: true,
                                omit: !(authenticatedUser.isAdmin || authenticatedUser.permissions.camera_watch_video_analysis),
                                cell: row => {
                                    if (row.status == 'success') {
                                        return <DropdownButton actions={[{
                                            content: translate('viewInformation'),
                                            onPress: () => navigation.navigate('Cameras', { screen: 'VideoAnalysisDetails', params: { id: row.id } })
                                        }]} />;
                                    }
                                }
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={queryResult.isInitialLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    statusStyle: (status: VideoAnalysisStatus) => {
        return {
            color: status === 'success' ? theme.colors.successes['500'] : theme.colors.errors['500']
        };
    },
}));

export default VideoAnalysis;
